import type { ColumnTag } from '../schemas'

export const kPresetTagColors: readonly ColumnTag['color'][] = ['Black', 'Blue', 'Green', 'Yellow', 'Orange', 'Red', 'Neutral']

export function nextTagColor(existingCount: number): ColumnTag['color'] {
  return kPresetTagColors[existingCount % kPresetTagColors.length]
}

export function leastUsedTagColor(tags: readonly ColumnTag[]): ColumnTag['color'] {
  const counts = new Map(kPresetTagColors.map((color) => [color, 0]))
  for (const tag of tags) {
    if (!tag.custom_color) counts.set(tag.color, (counts.get(tag.color) ?? 0) + 1)
  }
  return kPresetTagColors.reduce((least, color) => (counts.get(color)! < counts.get(least)! ? color : least))
}

function hslToRgb(hue: number, saturation: number, lightness: number): number[] {
  const amplitude = saturation * Math.min(lightness, 1 - lightness)
  return [0, 8, 4].map((offset) => {
    const channel = (offset + hue / 30) % 12
    return lightness - amplitude * Math.max(-1, Math.min(channel - 3, 9 - channel, 1))
  })
}

function luminance(rgb: number[]): number {
  const linear = rgb.map((channel) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
  return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722
}

/** Preserve the selected hue and saturation, with AA text against the 93% lightness fill. */
export function customTagColors(hex: string): { backgroundColor: string; borderColor: string; color: string } {
  const rgb = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255)
  const max = Math.max(...rgb)
  const min = Math.min(...rgb)
  const delta = max - min
  const lightness = (max + min) / 2
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1))
  let hue = 0
  if (delta !== 0) {
    if (max === rgb[0]) hue = ((rgb[1] - rgb[2]) / delta) % 6
    else if (max === rgb[1]) hue = (rgb[2] - rgb[0]) / delta + 2
    else hue = (rgb[0] - rgb[1]) / delta + 4
    hue = (hue * 60 + 360) % 360
  }
  const backgroundLuminance = luminance(hslToRgb(hue, saturation, 0.93))
  let textLightness = Math.min(lightness, 0.93)
  while ((backgroundLuminance + 0.05) / (luminance(hslToRgb(hue, saturation, textLightness)) + 0.05) < 4.5) {
    textLightness = Math.max(0, textLightness - 0.001)
  }
  return {
    backgroundColor: `hsl(${hue} ${saturation * 100}% 93%)`,
    borderColor: `hsl(${hue} ${saturation * 100}% 78%)`,
    color: `hsl(${hue} ${saturation * 100}% ${textLightness * 100}%)`,
  }
}
